import { Option } from '@sapphire/result';

import { SurveyQualificationCondition } from '../models/survey-qualification.model';

/**
 * Condition-code → display label, however the caller's catalog happens to spell it: core and
 * location qualifications read it off `Qualification.data`, advanced targeting off the
 * question's own `options`, and a location search supplies the labels it just resolved.
 */
export type ConditionLabels = ReadonlyMap<string, string>;

export function conditionLabelsFrom(
  conditions: readonly SurveyQualificationCondition[],
): ConditionLabels {
  return new Map(conditions.map(({ id, name }) => [id, name]));
}

/**
 * The selected conditions, in `conditionCodes` order, for a selectable survey qualification.
 *
 * This is the single place the "`data` holds exactly what is selected" invariant is produced.
 * It used to be re-asserted defensively at every read (`data.filter(d => codes.includes(d.id))`)
 * because writers carried stale data forward; deriving it here instead means the two fields
 * cannot drift, and readers can trust `data` as-is.
 *
 * A code with no label anywhere is omitted rather than shipped nameless — the same outcome the
 * old read-side filter produced. It should not arise: the catalog is what renders the options a
 * code can be chosen from in the first place.
 */
export function selectedConditions(
  conditionCodes: readonly string[],
  labels: ConditionLabels,
): SurveyQualificationCondition[] {
  return conditionCodes.flatMap((id) =>
    Option.from(labels.get(id)).mapOr<
      SurveyQualificationCondition[],
      SurveyQualificationCondition[]
    >([], (name) => [{ id, name }]),
  );
}

/** The wire's `condition_names` shape: one single-entry object per selected condition. */
export function toConditionNamePairs(
  conditions: readonly SurveyQualificationCondition[],
): Record<string, string>[] {
  return conditions.map(({ id, name }) => ({ [id]: name }));
}
